import { supabase } from '../supabase';
import type { Client } from '../types';
import {
  exactLikePattern,
  matchReasons,
  normFiscal,
  phoneVariants,
  searchableEmail,
  type DuplicateCriteria,
} from './duplicateMatching';

/** Stessa email su una posizione che non e' una scheda lead/cliente. */
export interface DuplicateOtherMatch {
  kind: 'contatto' | 'segnalatore' | 'utente';
  id: string;
  name: string;
  email: string;
}

export interface DuplicateDetectionResult {
  duplicates: Array<Client & {
    match_reason: string[];
    full_address?: string;
  }>;
  /** Contatti aggiuntivi, segnalatori e utenti con la stessa email */
  others: DuplicateOtherMatch[];
  has_duplicates: boolean;
}

export interface DuplicateSearchCriteria extends DuplicateCriteria {
  business_name?: string;
}

const LIMIT = 10;

function fullAddress(client: any): string | undefined {
  if (!client.street_address || !client.city) return undefined;
  const street = client.street_address + (client.street_number ? ` ${client.street_number}` : '');
  const city = [client.postal_code, client.city, client.province ? `(${client.province})` : ''].filter(Boolean).join(' ');
  return [street, city].filter(Boolean).join(', ');
}

/**
 * Possibili doppioni di un lead/cliente nell'organizzazione dell'utente.
 * Schede lead/clienti (non cancellate) per email, telefono, P.IVA o codice fiscale;
 * per la sola email anche contatti aggiuntivi, segnalatori e utenti.
 * `excludeClientId`: la scheda in modifica, che non e' doppione di se stessa.
 */
export async function searchForDuplicates(
  criteria: DuplicateSearchCriteria,
  options: { excludeClientId?: string | null } = {},
): Promise<DuplicateDetectionResult> {
  const empty: DuplicateDetectionResult = { duplicates: [], others: [], has_duplicates: false };
  const email = searchableEmail(criteria.email);
  const phones = phoneVariants(criteria.phone);
  const vat = normFiscal(criteria.vat_number);
  const cf = normFiscal(criteria.tax_code);
  if (!email && phones.length === 0 && !vat && !cf) return empty;

  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('User not authenticated');
  const { data: userProfile } = await supabase
    .from('user_profiles')
    .select('organization_id')
    .eq('id', userData.user.id)
    .single();
  if (!userProfile) throw new Error('User profile not found');
  const orgId = userProfile.organization_id;
  const exclude = options.excludeClientId || null;

  const clientsQuery = () => {
    let q = supabase.from('clients').select('*').eq('organization_id', orgId).is('deleted_at', null);
    if (exclude) q = q.neq('id', exclude);
    return q;
  };

  // Una ricerca per campo: niente .or() con valori da quotare (email con virgole, ILIKE con _)
  const searches: PromiseLike<{ data: any[] | null; error: any }>[] = [];
  if (email) searches.push(clientsQuery().ilike('email', exactLikePattern(email)).limit(LIMIT));
  if (phones.length) searches.push(clientsQuery().in('phone', phones).limit(LIMIT));
  if (vat) searches.push(clientsQuery().eq('vat_number', vat).limit(LIMIT));
  if (cf) searches.push(clientsQuery().ilike('tax_code', exactLikePattern(cf)).limit(LIMIT));

  const otherSearches = email
    ? [
        supabase.from('client_contacts').select('id, name, email, client_id')
          .eq('organization_id', orgId).eq('is_active', true).ilike('email', exactLikePattern(email)).limit(LIMIT),
        supabase.from('referrers').select('id, full_name, email')
          .eq('organization_id', orgId).ilike('email', exactLikePattern(email)).limit(LIMIT),
        supabase.from('user_profiles').select('id, full_name, email')
          .eq('organization_id', orgId).ilike('email', exactLikePattern(email)).limit(LIMIT),
      ]
    : [];

  const [clientResults, otherResults] = await Promise.all([Promise.all(searches), Promise.all(otherSearches)]);

  const byId = new Map<string, any>();
  for (const r of clientResults) {
    if (r.error) { console.error('[duplicateDetection] ricerca schede:', r.error); continue; }
    for (const c of r.data || []) byId.set(c.id, c);
  }
  const duplicates = Array.from(byId.values())
    .map((client) => ({ ...client, match_reason: matchReasons(client, criteria), full_address: fullAddress(client) }))
    .filter((c) => c.match_reason.length > 0)
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  const others: DuplicateOtherMatch[] = [];
  const kinds: DuplicateOtherMatch['kind'][] = ['contatto', 'segnalatore', 'utente'];
  otherResults.forEach((r, i) => {
    if (r.error) { console.error(`[duplicateDetection] ricerca ${kinds[i]}:`, r.error); return; }
    for (const row of (r.data || []) as any[]) {
      // Il contatto aggiuntivo della scheda in modifica non e' un doppione
      if (kinds[i] === 'contatto' && exclude && row.client_id === exclude) continue;
      others.push({ kind: kinds[i], id: row.id, name: row.name || row.full_name || row.email, email: row.email });
    }
  });

  return { duplicates, others, has_duplicates: duplicates.length > 0 || others.length > 0 };
}
